import { alpha } from '@mui/system/colorManipulator';
import {
  MAX_AI_CHAT_CHOICES,
  U21AiChatSingleSelectInnerProps,
} from 'app/shared/u21-ui/components/input/chat/shared';

import { U21Button } from 'app/shared/u21-ui/components/input/U21Button';
import { U21Select } from 'app/shared/u21-ui/components/input/select/U21Select';
import { U21TextField } from 'app/shared/u21-ui/components/input/text-field/U21TextField';
import { U21Radio } from 'app/shared/u21-ui/components/input/radio/U21Radio';
import { U21Spacer } from 'app/shared/u21-ui/components/layout/U21Spacer';
import { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

export const U21AiChatSingleSelectInner = ({
  choices,
  disabled,
  initialValues,
  onSubmit,
}: U21AiChatSingleSelectInnerProps) => {
  const choiceSet = useMemo(() => new Set(choices), [choices]);

  const [selected, setSelected] = useState<string | undefined>(() =>
    initialValues && choiceSet.has(initialValues) ? initialValues : undefined,
  );
  const [customText, setCustomText] = useState(() =>
    initialValues && !choiceSet.has(initialValues) ? initialValues : '',
  );

  const buildValue = useCallback(
    (sel: string | undefined, text: string): string => {
      const trimmed = text.trim();
      if (trimmed) return trimmed;
      return sel ?? '';
    },
    [],
  );

  const handleSelect = useCallback((choice: string) => {
    setSelected(choice);
    setCustomText('');
  }, []);

  const handleTextChange = useCallback((value: string) => {
    setCustomText(value);
    if (value.trim()) {
      setSelected(undefined);
    }
  }, []);

  const hasValue = useMemo(
    () => selected != null || customText.trim().length > 0,
    [selected, customText],
  );

  return (
    <>
      {choices.length > MAX_AI_CHAT_CHOICES ? (
        <U21Select
          disabled={disabled}
          value={selected}
          onChange={(value) => handleSelect(String(value ?? ''))}
          options={choices.map((choice) => ({
            value: choice,
            text: choice,
          }))}
          placeholder="Select an option..."
        />
      ) : (
        <U21Spacer spacing={1}>
          {choices.map((choice) => {
            const isSelected = selected === choice;
            return (
              <ChoiceRow
                horizontal
                key={choice}
                $selected={isSelected}
                $disabled={disabled}
                onClick={disabled ? undefined : () => handleSelect(choice)}
              >
                <U21Radio
                  checked={isSelected}
                  disabled={disabled}
                  label={choice}
                  value={choice}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => handleSelect(choice)}
                />
              </ChoiceRow>
            );
          })}
        </U21Spacer>
      )}
      <U21TextField
        type="textarea"
        disabled={disabled}
        value={customText}
        onChange={(value: string) => handleTextChange(value)}
        placeholder="Or add something else..."
        minRows={1}
      />
      <StyledSubmitButton
        color="primary"
        variant="contained"
        size="small"
        onClick={() => onSubmit(buildValue(selected, customText))}
        disabled={disabled || !hasValue}
      >
        Submit
      </StyledSubmitButton>
    </>
  );
};

const ChoiceRow = styled(U21Spacer)<{
  $selected: boolean;
  $disabled?: boolean;
}>`
  align-items: flex-start;
  border: 1px solid
    ${({ theme, $selected }) =>
      $selected ? theme.palette.primary.main : theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme, $selected, $disabled }) => {
    if ($disabled) {
      return theme.palette.action.disabledBackground;
    }

    if ($selected) {
      return alpha(theme.palette.primary.main, 0.08);
    }

    return theme.palette.background.paper;
  }};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  transition:
    border-color 0.15s,
    background-color 0.15s;

  &:hover {
    border-color: ${({ theme, $disabled }) =>
      $disabled ? undefined : theme.palette.primary.main};
  }
`;

const StyledSubmitButton = styled(U21Button)`
  width: fit-content;
`;
